import { queryOptions, useQuery } from '@tanstack/react-query'

import { apiFetch } from '@/lib/api'

/*
 * A aba Camadas da Área do Gestor. É estado de servidor: a lista vive no cache
 * do TanStack Query e as ações (enviar, testar, publicar) a invalidam, então a
 * tela mostra o que a API tem, e não uma cópia que ela mesma editou.
 */

/** Onde a camada aparece: nas duas leituras (por imóvel e por território) ou só por imóvel. */
export type OndeAparece = 'as-duas-leituras' | 'por-imovel'

/** Uma camada do catálogo do IIS e de referência, que a gestão só lista. */
export interface ItemDoCatalogo {
  codigo: string
  nome: string
  ondeAparece: OndeAparece
  situacao: 'ativo' | 'nao-entregue'
}

/**
 * O arquivo que espera na fila sobre uma camada que já existe: a inclusão do
 * arquivo de uma variável (com a ajuda e a cor dela) ou a troca do arquivo de
 * uma camada no ar (sem ajuda nem cor, que ficam as da camada).
 */
export interface EnvioDaCamada {
  situacao: 'aguardando-teste' | 'testada'
  ajuda: string | null
  cor: string | null
  enviadoPor: string
}

/** Uma camada enviada pela gestão: da fila ao mapa, e depois fora dele. */
export interface CamadaDaGestao {
  id: number
  nome: string
  ajuda: string
  cor: string
  ondeAparece: 'por-imovel'
  situacao: 'aguardando-teste' | 'testada' | 'publicada' | 'desativada'
  enviadaPor: string
  /**
   * A camada guarda desenho: o do ar, ou o de antes de ser desativada. A
   * desativada só oferece "Ver o desenho" e só volta ao mapa ao reativar com ele.
   */
  temDesenho: boolean
  /** Só na camada publicada com a troca do arquivo na fila. */
  envio?: EnvioDaCamada
}

/**
 * Uma das sete variáveis temáticas, que a gestão administra como uma camada
 * dela. Traz o código do catálogo público e, enquanto ninguém as escolheu,
 * fica sem ajuda e sem cor; não tem autor, porque nasce com a instalação.
 */
export interface VariavelDaGestao {
  id: number
  codigo: string
  nome: string
  ajuda: string | null
  cor: string | null
  ondeAparece: 'por-imovel'
  situacao: 'nao-entregue' | CamadaDaGestao['situacao']
  enviadaPor: string | null
  /** Ganha desenho só quando o arquivo dela é publicado; o envio na fila não conta. */
  temDesenho: boolean
  /** Só com o arquivo dela (ou a troca dele) na fila. */
  envio?: EnvioDaCamada
}

/** O corpo de `GET /v1/admin/camadas`, como a API o manda. */
export interface RespostaDaAba {
  catalogo: ItemDoCatalogo[]
  /** Primeiro as variáveis temáticas, na ordem do painel; depois as enviadas, na do envio. */
  gestao: (CamadaDaGestao | VariavelDaGestao)[]
  /** Só o administrador testa e publica; o editor de camadas só envia. */
  podeTestarEPublicar: boolean
}

/**
 * A aba como a tela a usa: as camadas enviadas de um lado e as variáveis do
 * outro. Enquanto a tela lista as variáveis pelo catálogo, é aqui que elas
 * ficam à mão, com o id que as ações da gestão pedem.
 */
export interface CamadasDaGestao {
  catalogo: ItemDoCatalogo[]
  gestao: CamadaDaGestao[]
  variaveis: VariavelDaGestao[]
  podeTestarEPublicar: boolean
}

function ehVariavel(item: CamadaDaGestao | VariavelDaGestao): item is VariavelDaGestao {
  return 'codigo' in item && typeof item.codigo === 'string'
}

/** Separa as variáveis temáticas das camadas enviadas, mantendo a ordem da API em cada lado. */
export function separarVariaveis(resposta: RespostaDaAba): CamadasDaGestao {
  return {
    catalogo: resposta.catalogo,
    gestao: resposta.gestao.filter((item): item is CamadaDaGestao => !ehVariavel(item)),
    variaveis: resposta.gestao.filter(ehVariavel),
    podeTestarEPublicar: resposta.podeTestarEPublicar,
  }
}

/** Uma linha da gestão, com id: uma camada enviada ou uma das variáveis temáticas. */
export type CamadaGerida = CamadaDaGestao | VariavelDaGestao

export function ehVariavelDaGestao(camada: CamadaGerida): camada is VariavelDaGestao {
  return ehVariavel(camada)
}

/** A situação da linha na lista, como a tela a escreve: "Ativo" é a camada no ar. */
export type SituacaoNaLista = 'ativo' | 'nao-entregue' | 'desativada'

/** Uma linha da tabela "Lista de camadas", venha do catálogo ou da gestão. */
export interface LinhaDaLista {
  /** Chave de lista: o código do catálogo e o id da gestão nunca colidem. */
  chave: string
  nome: string
  ondeAparece: OndeAparece
  situacao: SituacaoNaLista
  /**
   * A linha da gestão por trás desta, quando há: a variável temática e a
   * camada enviada que saiu da fila. É ela que leva o menu; o VC, os limites
   * e as referências não têm.
   */
  camada?: CamadaGerida
}

const CHAVE = ['admin', 'camadas'] as const

/** Sem nova tentativa: erro de servidor deve aparecer logo, com o botão "Tentar de novo". */
export const camadasDaGestaoQuery = queryOptions({
  queryKey: CHAVE,
  queryFn: async () => separarVariaveis(await apiFetch<RespostaDaAba>('/v1/admin/camadas')),
  retry: false,
})

export function useCamadasDaGestao() {
  return useQuery(camadasDaGestaoQuery)
}

/** O que a fila e a tabela contam: o substantivo no singular e no plural ("camada", "camadas"). */
export type Substantivo = readonly [singular: string, plural: string]

const CAMADA: Substantivo = ['camada', 'camadas']

/** "1 camada", "0 camadas", "9 camadas"; com outro substantivo, "1 tooltip", "2 tooltips". */
export function contagemDe(total: number, [singular, plural]: Substantivo): string {
  return `${total} ${total === 1 ? singular : plural}`
}

/** "1 camada", "0 camadas", "9 camadas". */
export function contagemDeCamadas(total: number): string {
  return contagemDe(total, CAMADA)
}

/**
 * O título da fila. Para o administrador a fila é trabalho dele ("precisa da
 * sua revisão"); para o editor é só uma espera ("aguardando o administrador").
 * A fila de tooltips usa o mesmo título, com o substantivo dela.
 */
export function tituloDaFila(
  total: number,
  podeTestarEPublicar: boolean,
  substantivo: Substantivo = CAMADA
): string {
  if (podeTestarEPublicar) {
    return `${contagemDe(total, substantivo)} ${total === 1 ? 'precisa' : 'precisam'} da sua revisão`
  }
  return `${contagemDe(total, substantivo)} aguardando o administrador`
}

/**
 * O que espera na fila: a camada nova inteira, ou o arquivo enviado sobre uma
 * camada que já existe (a inclusão do arquivo de uma variável não entregue, a
 * troca do arquivo de uma camada no ar).
 */
export type TipoDoEnvio = 'nova' | 'inclusao' | 'troca'

/** Um item da fila de revisão. */
export interface PendenteDaFila {
  /** O id da camada: é sobre ele que se testa, publica e cancela. */
  id: number
  nome: string
  situacao: 'aguardando-teste' | 'testada'
  tipo: TipoDoEnvio
}

/**
 * A fila: as camadas novas que esperam o teste ou a publicação e os arquivos
 * enviados sobre camadas que já existem, na ordem da API (as variáveis
 * primeiro). Uma camada com arquivo na fila continua na lista, como o público a
 * vê; na fila entra só o arquivo.
 */
export function pendentesDaFila(
  dados: Pick<CamadasDaGestao, 'gestao' | 'variaveis'>
): PendenteDaFila[] {
  return [...dados.variaveis, ...dados.gestao].flatMap<PendenteDaFila>((camada) => {
    if (camada.envio !== undefined) {
      return [
        {
          id: camada.id,
          nome: camada.nome,
          situacao: camada.envio.situacao,
          tipo: camada.situacao === 'publicada' ? 'troca' : 'inclusao',
        },
      ]
    }
    if (camada.situacao === 'aguardando-teste' || camada.situacao === 'testada') {
      return [{ id: camada.id, nome: camada.nome, situacao: camada.situacao, tipo: 'nova' }]
    }
    return []
  })
}

function situacaoNaLista(camada: CamadaGerida): SituacaoNaLista | undefined {
  switch (camada.situacao) {
    case 'publicada':
      return 'ativo'
    case 'desativada':
      return 'desativada'
    case 'nao-entregue':
      return 'nao-entregue'
    default:
      return undefined
  }
}

/**
 * A tabela: o catálogo na ordem da API, com cada variável temática numa linha
 * só, a da gestão (que leva o menu), e, depois do catálogo, as camadas novas que
 * já saíram da fila (no ar, "Ativo", ou fora dele, "Desativada").
 *
 * A variável desativada sai do catálogo público, mas continua na lista, no
 * lugar dela entre as variáveis. Por isso o bloco das variáveis é montado pela
 * ordem da gestão, que é a do painel, e entra onde o catálogo tinha a primeira
 * delas; se o catálogo não tem nenhuma (todas desativadas), entra logo depois
 * da primeira linha, que é o Valor de Conservação.
 */
export function linhasDaLista(dados: CamadasDaGestao): LinhaDaLista[] {
  const codigos = new Set(dados.variaveis.map((v) => v.codigo))
  const doCatalogo = dados.catalogo.map<LinhaDaLista>((item) => ({
    chave: `catalogo-${item.codigo}`,
    nome: item.nome,
    ondeAparece: item.ondeAparece,
    situacao: item.situacao,
  }))
  const primeira = dados.catalogo.findIndex((item) => codigos.has(item.codigo))
  const lugar = primeira === -1 ? Math.min(1, doCatalogo.length) : primeira
  const semVariaveis = doCatalogo.filter((_, i) => !codigos.has(dados.catalogo[i]?.codigo ?? ''))
  const lugarSemVariaveis = doCatalogo
    .slice(0, lugar)
    .filter((_, i) => !codigos.has(dados.catalogo[i]?.codigo ?? '')).length

  const variaveis = dados.variaveis.flatMap<LinhaDaLista>((variavel) => {
    const item = dados.catalogo.find((i) => i.codigo === variavel.codigo)
    const situacao = situacaoNaLista(variavel)
    if (situacao === undefined) return []
    return [
      {
        chave: `catalogo-${variavel.codigo}`,
        nome: item?.nome ?? variavel.nome,
        ondeAparece: item?.ondeAparece ?? variavel.ondeAparece,
        situacao,
        camada: variavel,
      },
    ]
  })

  const daGestao = dados.gestao.flatMap<LinhaDaLista>((camada) => {
    const situacao = situacaoNaLista(camada)
    if (situacao === undefined) return []
    return [
      {
        chave: `gestao-${camada.id}`,
        nome: camada.nome,
        ondeAparece: camada.ondeAparece,
        situacao,
        camada,
      },
    ]
  })

  return [
    ...semVariaveis.slice(0, lugarSemVariaveis),
    ...variaveis,
    ...semVariaveis.slice(lugarSemVariaveis),
    ...daGestao,
  ]
}

/**
 * O código com que o mapa público liga a camada (`/?camada=`): o da variável
 * no catálogo, ou `gestao-{id}` para a camada nova.
 */
export function codigoNoMapa(camada: CamadaGerida): string {
  return ehVariavel(camada) ? camada.codigo : `gestao-${camada.id}`
}

export function rotuloDeOndeAparece(onde: OndeAparece): string {
  return onde === 'as-duas-leituras' ? 'As duas leituras' : 'Por imóvel'
}
